import { http } from '@/core/http/client'
import type {
  ConfigRecord,
  DictDataRecord,
  DictTypeRecord,
  HospitalOption,
  HospitalRecord,
  MenuTreeNode,
  PageResult,
  RoleOption,
  RoleRecord,
  SystemFileUploadResult,
  UserRecord
} from '@/models/system'
import type { DataSettingRecord } from '@/models/business'

// 查询所有用户角色
export const getRoleOptions = (params?: unknown) => http.get<RoleOption[]>('/sysRole/list', params)

interface OrganizationOptionRecord {
  orgId?: string | number
  organizationId?: string | number
  id?: string | number
  orgName?: string
  organizationName?: string
  name?: string
}

type OrganizationOptionResponse =
  | OrganizationOptionRecord[]
  | PageResult<OrganizationOptionRecord>

// 所属医院下拉框接口：GET /sysOrganization/list
export async function getHospitalOptions(params?: unknown): Promise<HospitalOption[]> {
  const response = await http.get<OrganizationOptionResponse>('/sysOrganization/list', params)
  const organizations = Array.isArray(response) ? response : response.records || []

  return organizations.flatMap((organization) => {
    const orgId = organization.orgId ?? organization.organizationId ?? organization.id
    const orgName = organization.orgName ?? organization.organizationName ?? organization.name

    if (orgId === undefined || orgName === undefined) return []
    return [{ orgId, orgName }]
  })
}

export const listUsers = (params?: unknown) => http.get<PageResult<UserRecord>>('/sysUser/page', params)
export const getUser = (id: string | number) => http.get<UserRecord>(`/sysUser/getInfo/${id}`)
// 重置密码
export const resetUserPassword = (data: unknown) => http.put('/sysUser/reset/password', data)
// 删除用户
export const deleteUser = (id: string | number) => http.delete(`/sysUser/remove/${id}`)
// 新增用户
export const createUser = (data: unknown) => http.post('/sysUser/save', data)
// 编辑用户
export const updateUser = (data: unknown) => http.put('/sysUser/update', data)

// 获取角色权限树
export const getMenuTree = (params?: unknown) => http.get<MenuTreeNode[]>('/sysMenu/list', params)
// 角色列表
export const listRoles = (params?: unknown) => http.get<PageResult<RoleRecord>>('/sysRole/page', params)
// 获取角色详情
export const getRole = (id: string | number) =>
  http.get<{ roleName?: string; roleKey?: string; remark?: string; menuIds?: Array<string | number> }>(`/sysRole/getInfo/${id}`)
// 删除角色
export const deleteRole = (id: string | number) => http.delete(`/sysRole/remove/${id}`)
// 新增角色
export const createRole = (data: unknown) => http.post('/sysRole/save', data)
// 编辑角色
export const updateRole = (data: unknown) => http.put('/sysRole/update', data)

// 参数设置列表
export const listConfigs = (params?: unknown) => http.get<PageResult<ConfigRecord>>('/sysConfig/page', params)
export const getConfigByCode = (code: string) => http.get<ConfigRecord>('/sysConfig/code', { code })
// 参数编辑
export const updateConfig = (data: unknown) => http.put('/sysConfig/update', data)
// 参数删除
export const deleteConfig = (id: string | number) => http.delete(`/sysConfig/remove/${id}`)
// 参数新增
export const createConfig = (data: unknown) => http.post('/sysConfig/save', data)

// 字典列表
export const listDictTypes = (params?: unknown) => http.get<PageResult<DictTypeRecord>>('/sysDictType/page', params)
// export const getDictType = (id: string | number) => http.get<DictTypeRecord>(`/dict/type/${id}`)
// 删除字典
export const deleteDictType = (id: string | number) => http.delete(`/sysDictType/remove/${id}`)
// 新增字典
export const createDictType = (data: unknown) => http.post('/sysDictType/save', data)
// 更新字典
export const updateDictType = (data: unknown) => http.put('/sysDictType/update', data)
// 字典数据列表
export const listDictData = (params?: unknown) => http.get<PageResult<DictDataRecord>>('/sysDictData/page', params)
// 根据字典类型查询字典子项
export const listDictDataByType = (dictType: string) => http.get<DictDataRecord[]>('/sysDictData/list', { dictType })
// export const getDictData = (id: string | number) => http.get<DictDataRecord>(`/dict/data/${id}`)
export const deleteDictData = (id: string | number) => http.delete(`/sysDictData/remove/${id}`)
export const createDictData = (data: unknown) => http.post('/sysDictData/save', data)
export const updateDictData = (data: unknown) => http.put('/sysDictData/update', data)

export const listDataSettings = (params?: unknown) => http.get<DataSettingRecord[]>('/dataSettings/list', params)
export const updateDataSettings = (data: unknown) => http.put('/dataSettings', data)
export const uploadSystemFile = (data: FormData) =>
  http.uploadPost<SystemFileUploadResult>('/upload', data, { skipLoading: true })

// 医院管理列表
export const listHospitals = (params?: unknown) => http.get<PageResult<HospitalRecord>>('/sysOrganization/page', params)
// 编辑
export const updateHospital = (data: unknown) => http.put('/sysOrganization/update', data)
// 新增
export const createHospital = (data: unknown) => http.post('/sysOrganization/save', data)
// 删除
export const deleteHospital = (orgId: string | number) => http.delete(`/sysOrganization/remove/${orgId}`)
